import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { extend, is } from 'quasar'
import { applyPatch } from 'fast-json-patch'
import type { Operation } from 'fast-json-patch'
import type { ImportMapping } from 'models/ImportMapping'
import type { EnvironmentTwin } from 'models/EnvironmentTwin'
import type { FeatureTwin, MainFeatureTwin } from 'models/FeatureTwin'
import type {
  FeatureConfig,
  MainFeatureConfig,
  ExtendedMainFeatureConfig,
  DrawerFeatureConfig,
  ContextMenuItemsConfig
} from 'models/FeatureConfig'
import type { FeatureQueryTwin } from 'models/FeatureQueryTwin'
import type { TwinMapping } from 'models/TwinMapping'
import type { DashboardTileTwin } from 'components/features/DashboardFeature'
import type { RoleRelationship } from 'services/UsersApiService'
import { arrayMoveItem, sortCallback } from 'utils/arrays'
import { envApiService, adminApiService } from 'services/AdminApiService'
import allFeatureConfigs from 'components/features'
import { useLocalStorage } from 'composables/use-local-storage'

// ============================================================================
// Permission Types and Definitions
// ============================================================================

/**
 * All available permissions in the system.
 * Follows Azure Digital Twins permission format: resource/action or resource/subresource/action
 * Wildcard (*) means any action is allowed for that scope.
 */
export type Permission =
  // Digital Twins (read, write, delete, wildcard)
  | 'digitaltwins/read'
  | 'digitaltwins/write'
  | 'digitaltwins/delete'
  | 'digitaltwins/commands/action'
  | 'digitaltwins/commands/*'
  | 'digitaltwins/relationships/read'
  | 'digitaltwins/relationships/write'
  | 'digitaltwins/relationships/delete'
  | 'digitaltwins/relationships/*'
  | 'digitaltwins/*'
  // Models
  | 'models/read'
  | 'models/write'
  | 'models/delete'
  | 'models/*'
  // Query
  | 'query/action'
  | 'query/*'
  // Jobs
  | 'jobs/imports/read'
  | 'jobs/imports/write'
  | 'jobs/imports/delete'
  | 'jobs/imports/cancel/action'
  | 'jobs/imports/*'
  | 'jobs/deletions/read'
  | 'jobs/deletions/write'
  | 'jobs/deletions/delete'
  | 'jobs/deletions/cancel/action'
  | 'jobs/deletions/*'
  | 'jobs/*'
  // Environment
  | 'environment/read'
  | 'environment/write'
  | 'environment/delete'
  | 'environment/*'
  // Features
  | 'features/read'
  | 'features/write'
  | 'features/delete'
  | 'features/*'
  // Import Mappings
  | 'importmappings/read'
  | 'importmappings/write'
  | 'importmappings/delete'
  | 'importmappings/*'
  // Users
  | 'users/read'
  | 'users/write'
  | 'users/delete'
  | 'users/*'
  // Flows
  | 'flows/read'
  | 'flows/write'
  | 'flows/delete'
  | 'flows/execute'
  | 'flows/*'
  // Admin wildcard
  | 'admin/*'

/**
 * Permission definitions per role.
 * When a user has multiple roles, permissions are accumulated.
 * This is the default/fallback - should be loaded from backend via /roles endpoint.
 */
const DEFAULT_ROLE_PERMISSIONS: Record<string, string[]> = {
  reader: [
    'digitaltwins/read',
    'digitaltwins/relationships/read',
    'models/read',
    'query/action',
    'environment/read',
    'features/read',
    'importmappings/read',
    'users/read',
    'flows/read'
  ],
  contributor: [
    'digitaltwins/*',
    'digitaltwins/relationships/*',
    'models/read',
    'query/action',
    'environment/read',
    'features/read',
    'importmappings/read',
    'users/read',
    'flows/read',
    'flows/execute'
  ],
  owner: [
    'digitaltwins/*',
    'digitaltwins/relationships/*',
    'digitaltwins/commands/*',
    'models/*',
    'query/action',
    'environment/*',
    'features/*',
    'importmappings/*',
    'users/*',
    'flows/*',
    'jobs/imports/*',
    'secrets/read',
    'apikeys/read',
    'devices/read',
    'certificates/read',
    'dataservices/read'
  ],
  admin: [
    'digitaltwins/*',
    'models/*',
    'query/*',
    'environment/*',
    'features/*',
    'importmappings/*',
    'users/*',
    'flows/*',
    'jobs/*',
    'secrets/*',
    'apikeys/*',
    'devices/*',
    'certificates/*',
    'dataservices/*',
    'admin/*'
  ],
  ApplicationAdministrator: [
    'digitaltwins/*',
    'models/*',
    'query/*',
    'environment/*',
    'features/*',
    'importmappings/*',
    'users/*',
    'flows/*',
    'jobs/*',
    'secrets/*',
    'apikeys/*',
    'devices/*',
    'certificates/*',
    'dataservices/*',
    'admin/*'
  ]
}

// ============================================================================
// Feature Config Types
// ============================================================================

export interface FeatureConfigs {
  adminMainConfigs: MainFeatureConfig[]
  adminDrawerConfigs: DrawerFeatureConfig[]
  adminContextMenuItemsConfigs: ContextMenuItemsConfig[]
  mainConfigs: MainFeatureConfig[]
  drawerConfigs: DrawerFeatureConfig[]
  contextMenuItemsConfigs: ContextMenuItemsConfig[]
}

// ============================================================================
// Store Definition
// ============================================================================

export const useGlobalStore = defineStore('global', () => {
  // ==========================================================================
  // State
  // ==========================================================================

  /** The current active environment ID (persisted) */
  const environmentId = useLocalStorage<string | undefined>('dt-env-id', undefined)
  /** All the environments the current user has access to (persisted) */
  const environments = useLocalStorage<EnvironmentTwin[]>('dt-environments', [])
  /** All features for the current environment (persisted, refreshed on every navigation) */
  const features = useLocalStorage<(FeatureTwin & { $order?: number })[]>('dt-features', [], 'v1')
  /**
   * All feature queries (and dashboard tiles) by feature id for the current environment
   * (persisted, refreshed on every navigation)
   */
  const featureQueries = useLocalStorage<{
    [featureId: string]: { [featureQueryId: string]: FeatureQueryTwin | DashboardTileTwin }
  }>('dt-feature-queries', {}, 'v1')
  /**
   * In-flight bulk preload promise for the current environment, used to deduplicate
   * concurrent calls to {@link preloadAllFeatureQueries} and to let
   * {@link ensureFeatureQueriesLoaded} await it instead of issuing a per-feature call.
   */
  const featureQueriesPreload = ref<Promise<void> | undefined>(undefined)
  /**
   * Invalidates an in-flight preload when environment-scoped state is reset.
   * A request started before an import or environment switch must not restore its
   * stale response after the reset has cleared the persisted cache.
   */
  const featureQueriesPreloadGeneration = ref(0)
  /**
   * Incremented by {@link preloadAllFeatureQueries} whenever a refresh brought
   * features/feature queries that actually differ from what's already in the store.
   *
   * Consumers that hold an imperative snapshot of this data (dashboards read their
   * tiles into a local `tiles` ref) watch this counter to know when to re-derive.
   * Watching a counter instead of deep-watching the collections keeps a large object
   * out of the reactivity hot path, and because a no-op refresh never bumps it, the
   * common case (nothing changed server-side) costs no re-render at all.
   */
  const featureDataRevision = ref(0)
  /** All import mappings for the current environment */
  const importMappings = ref<ImportMapping[]>([])
  /** All twin mappings by import mapping for the current environment */
  const twinMappings = ref<{ [importMappingId: string]: (TwinMapping & { $order?: number })[] }>({})
  /** The latest feature query map */
  const latestFeatureQueryMap = ref<{ featureId: string; featureQueryId: string } | undefined>(
    undefined
  )
  /** The currently active main feature id */
  const currentMainFeatureId = ref<string | undefined>(undefined)
  /** The currently active drawer feature id */
  const currentDrawerFeatureId = ref<string | undefined>(undefined)
  /** The current container SAS token */
  const containerSas = ref<string | undefined>(undefined)
  /** Role definitions loaded from backend, maps role name to list of permissions (persisted) */
  const roleDefinitions = useLocalStorage<Record<string, string[]>>(
    'dt-role-defs',
    DEFAULT_ROLE_PERMISSIONS
  )

  // ==========================================================================
  // Computed - Environment & Roles
  // ==========================================================================

  /** Currently loaded environment */
  const environment = computed(() =>
    environments.value.find((e) => e.$dtId === environmentId.value)
  )

  /** Whether the current environment is self-hosted (Kubernetes) */
  const isSelfHosted = computed(
    () =>
      (environment.value?.adtHost?.endsWith('cluster.local') ||
        environment.value?.adtHost?.endsWith('poweredby.arcadis.com')) ??
      false
  )

  /**
   * Returns all role relationships for the current environment.
   * Each user can have multiple roles on an environment.
   */
  const environmentRoles = computed<RoleRelationship[]>(() => environment.value?.$roles ?? [])

  /**
   * Returns unique role names for the current environment.
   * Extracts the 'role' property from each role relationship.
   */
  const environmentRoleNames = computed<string[]>(() => [
    ...new Set(environmentRoles.value.map((r) => r.role).filter(Boolean))
  ])

  /**
   * Accumulated permissions based on all user roles for the current environment.
   * When a user has multiple roles, all permissions are combined.
   * Uses roleDefinitions loaded from backend (falls back to DEFAULT_ROLE_PERMISSIONS).
   */
  const permissions = computed<Set<string>>(() => {
    const allPermissions = new Set<string>()
    for (const roleName of environmentRoleNames.value) {
      const rolePermissions = roleDefinitions.value[roleName]
      if (rolePermissions) {
        for (const permission of rolePermissions) {
          allPermissions.add(permission as Permission)
        }
      }
    }
    return allPermissions
  })

  /**
   * Check if the user has a specific permission.
   * Supports wildcard permissions (e.g., digitaltwins/* matches digitaltwins/read, digitaltwins/write, etc.)
   * @param permission The permission to check
   * @returns true if the user has the permission
   */
  const hasPermission = (permission: string): boolean => {
    // Fast path: exact match
    if (permissions.value.has(permission)) {
      return true
    }

    // Check wildcard permissions
    for (const userPermission of permissions.value) {
      if (userPermission.endsWith('/*')) {
        // Get the prefix without the wildcard (e.g., "digitaltwins/" from "digitaltwins/*")
        const prefix = userPermission.slice(0, -1) // Remove '*', keep '/'
        if (permission?.startsWith(prefix)) {
          return true
        }
      }
    }

    return false
  }

  /**
   * Check if the user has any of the specified permissions.
   * @param perms The permissions to check
   * @returns true if the user has at least one of the permissions
   */
  const hasAnyPermission = (...perms: Permission[]): boolean => perms.some((p) => hasPermission(p))

  /**
   * Check if the user has all of the specified permissions.
   * @param perms The permissions to check
   * @returns true if the user has all of the permissions
   */
  const hasAllPermissions = (...perms: Permission[]): boolean =>
    perms.every((p) => hasPermission(p))

  // ==========================================================================
  // Computed - Permission-based Access Control
  // ==========================================================================

  /** The user is allowed to edit twins */
  const editTwinAllowed = computed(() => hasPermission('digitaltwins/write'))

  /** The user is allowed to edit environment settings */
  const editEnvAllowed = computed(() => hasPermission('environment/write'))

  // ==========================================================================
  // Computed - Features
  // ==========================================================================

  /** Filtered feature configurations, formatted for easy component loading */
  const featureConfigs = computed<FeatureConfigs>(() => {
    const adminMainConfigs: MainFeatureConfig[] = []
    const adminDrawerConfigs: DrawerFeatureConfig[] = []
    const adminContextMenuItemsConfigs: ContextMenuItemsConfig[] = []

    const userRoles = environmentRoleNames.value

    for (const feature of features.value) {
      if (!feature) continue // needed for admin feature, if feature is deleted

      // The last part of the model id is the same as the name of the feature
      const featureName = feature.$metadata.$model.split(';')[0].split(':').slice(-1)[0]
      const featureConfig: FeatureConfig | undefined = allFeatureConfigs[featureName]
      if (!featureConfig) continue

      if (featureConfig.main) {
        adminMainConfigs.push({
          ...featureConfig.main,
          feature
        })
      }

      if (featureConfig.drawer) {
        adminDrawerConfigs.push({
          ...featureConfig.drawer,
          feature
        })
      }

      if (featureConfig.contextMenuItems) {
        adminContextMenuItemsConfigs.push({
          ...featureConfig.contextMenuItems,
          feature
        })
      }
    }

    /**
     * Filter features based on role configuration.
     * A feature is visible if:
     * - It has no roles defined (visible to all), OR
     * - At least one of the user's roles is enabled in the feature's roles config
     */
    const isFeatureAllowedForUser = (featureRoles?: { [key: string]: boolean }): boolean => {
      if (!featureRoles) return true // No role restriction, visible to all
      return userRoles.some((role) => featureRoles[role] === true)
    }

    return {
      /** The Admin feature configs are not filtered by role and are used in the Admin Features configuration pages */
      adminMainConfigs,
      /** The Admin feature configs are not filtered by role and are used in the Admin Features configuration pages */
      adminDrawerConfigs,
      adminContextMenuItemsConfigs,
      // These are used throughout the application and are filtered by role
      mainConfigs: adminMainConfigs.filter((c) => isFeatureAllowedForUser(c.feature?.roles)),
      drawerConfigs: adminDrawerConfigs.filter((c) => isFeatureAllowedForUser(c.feature?.roles)),
      contextMenuItemsConfigs: adminContextMenuItemsConfigs.filter((c) =>
        isFeatureAllowedForUser(c.feature?.roles)
      )
    }
  })

  const featureTree = computed<ExtendedMainFeatureConfig[]>(() =>
    getFeatureChildrenRecursive(featureConfigs.value.mainConfigs, 0, undefined, true)
  )

  const adminFeatureTree = computed<ExtendedMainFeatureConfig[]>(() =>
    getFeatureChildrenRecursive(featureConfigs.value.adminMainConfigs, 0, undefined, false, true)
  )

  /** The twin for the currently active main feature */
  const currentMainFeature = computed<MainFeatureTwin | undefined>(() =>
    features.value.find((f) => f.$dtId === currentMainFeatureId.value)
  )

  /** The configuration for the currently active main feature */
  const currentMainFeatureConfig = computed<MainFeatureConfig | undefined>(() =>
    featureConfigs.value.mainConfigs.find(
      (featureConfig) => currentMainFeatureId.value === featureConfig.feature?.$dtId
    )
  )

  /** The twin for the currently active drawer feature */
  const currentDrawerFeature = computed<FeatureTwin | undefined>(() =>
    features.value.find((f) => f.$dtId === currentDrawerFeatureId.value)
  )

  /** The configuration for the currently active drawer feature */
  const currentDrawerFeatureConfig = computed<DrawerFeatureConfig | undefined>(() =>
    featureConfigs.value.drawerConfigs.find(
      (featureConfig) => currentDrawerFeatureId.value === featureConfig.feature?.$dtId
    )
  )

  const latestFeatureQuery = computed(() => {
    if (
      latestFeatureQueryMap.value?.featureId &&
      featureQueries.value[latestFeatureQueryMap.value?.featureId]
    ) {
      return featureQueries.value[latestFeatureQueryMap.value?.featureId][
        latestFeatureQueryMap.value?.featureQueryId
      ]
    }
    return undefined
  })

  // ==========================================================================
  // Actions - Environments
  // ==========================================================================

  /** Load role definitions from backend */
  async function loadRoleDefinitions() {
    try {
      const definitions = await adminApiService.getRoleDefinitions()
      roleDefinitions.value = definitions
    } catch (error) {
      console.warn('Failed to load role definitions from backend, using defaults', error)
      // Keep using DEFAULT_ROLE_PERMISSIONS as fallback
    }
  }

  /** Get environment from server */
  async function getEnvironment(envId?: string) {
    const envIdx = environments.value.findIndex(
      (e: EnvironmentTwin) => e.$dtId === (envId || environmentId.value)
    )
    const env = await envApiService.getEnvironment(envId)
    if (envIdx > -1) {
      environments.value[envIdx] = env
    } else {
      environments.value.push(env)
      environments.value.sort((a, b) => `${a.name}`.localeCompare(`${b.name}`))
    }
    return env
  }

  /** Get environments from server */
  async function getEnvironments() {
    environments.value =
      (await envApiService.getEnvironments()).sort((a, b) =>
        `${a.name}`.localeCompare(`${b.name}`)
      ) || []
    return environments.value
  }

  /** Patch the current environment on the server */
  async function updateEnvironment(envId: string, patch: Operation[]) {
    const normalizedPatch = patch.map((op) =>
      (op.op === 'add' || op.op === 'replace') && 'value' in op && op.value === null
        ? { op: 'remove' as const, path: op.path }
        : op
    )
    await envApiService.updateEnvironment(envId, normalizedPatch)
    const envIdx = environments.value.findIndex((e: EnvironmentTwin) => e.$dtId === envId)
    if (envIdx > -1)
      environments.value[envIdx] = applyPatch<EnvironmentTwin>(
        environments.value[envIdx],
        normalizedPatch,
        false,
        false
      ).newDocument
  }

  /** Set the first drawer feature as the current */
  function setFirstDrawerFeature() {
    const drawerConfigsList = featureConfigs.value.drawerConfigs

    if (currentMainFeature.value?.loadDrawerFeatureId) {
      const drawerFeatureComp = drawerConfigsList.find(
        (fc) => fc.feature?.$dtId === currentMainFeature.value?.loadDrawerFeatureId
      )
      if (drawerFeatureComp && drawerFeatureComp.feature?.$dtId !== currentDrawerFeatureId.value) {
        currentDrawerFeatureId.value = drawerFeatureComp.feature?.$dtId
        return
      }
    }
    if (drawerConfigsList.length && drawerConfigsList[0].feature) {
      currentDrawerFeatureId.value = drawerConfigsList[0].feature.$dtId
    }
  }

  // ==========================================================================
  // Actions - Features
  // ==========================================================================

  /** Get features for the current environment from the server, and set current feature to the first */
  async function getFeatures(envId?: string) {
    const _environmentId = envId || environmentId.value
    if (!_environmentId) return []
    const nextFeatures = (await envApiService.getFeatures(_environmentId)) || []
    if (!is.deepEqual(features.value, nextFeatures)) {
      features.value = nextFeatures
      featureDataRevision.value++
    }

    const { drawerConfigs: drawerConfigsList } = featureConfigs.value
    if (drawerConfigsList.length && drawerConfigsList[0].feature)
      currentDrawerFeatureId.value = drawerConfigsList[0].feature.$dtId

    return features.value as FeatureTwin[]
  }

  /** Create or update a feature on the server */
  async function createOrUpdateFeature(twin: FeatureTwin) {
    await envApiService.createOrUpdateFeature(twin.$dtId, twin)
    const fIdx = features.value.findIndex((f) => f.$dtId === twin.$dtId)
    if (fIdx > -1) features.value[fIdx] = twin
    else features.value.push(twin)
    features.value.sort(sortCallback)
  }

  /** Delete feature on the server */
  async function deleteFeature(featureId: string) {
    await envApiService.deleteFeature(featureId)
    features.value = features.value.filter((f) => f.$dtId !== featureId)
    delete featureQueries.value[featureId]
  }

  /** Create or update a featureQuery on the server */
  async function createOrUpdateFeatureQuery(featureId: string, featureQuery: FeatureQueryTwin) {
    await envApiService.createOrUpdateFeatureQuery(featureId, featureQuery.$dtId, featureQuery)
    if (!featureQueries.value[featureId]) {
      featureQueries.value[featureId] = {}
    }
    featureQueries.value[featureId][featureQuery.$dtId] = featureQuery
  }

  /** Update (patch) a feature query on the server */
  async function updateFeatureQuery(featureId: string, featureQueryId: string, patch: Operation[]) {
    await envApiService.updateFeatureQuery(featureId, featureQueryId, patch)
    const featureQuery = featureQueries.value[featureId][featureQueryId]
    if (featureQuery) {
      featureQueries.value[featureId][featureQueryId] = applyPatch<FeatureQueryTwin>(
        featureQuery,
        patch,
        false,
        false
      ).newDocument
    }
  }

  /** Order a featureQuery on the server */
  async function orderFeatureQuery(
    featureQueryId: string,
    featureId: string,
    direction: 'up' | 'down' = 'down'
  ): Promise<FeatureTwin[]> {
    // FeatureList has already been ordered during retrieval
    const featureQueryList: (FeatureQueryTwin | DashboardTileTwin)[] = extend<
      (FeatureQueryTwin | DashboardTileTwin)[]
    >(true, [], Object.values(featureQueries.value[featureId])).sort(sortCallback)
    const twinIndex = featureQueryList.findIndex((f) => f.$dtId === featureQueryId)
    const newOrderedTwinList = arrayMoveItem([...featureQueryList], twinIndex, direction).map(
      (f, i) => ({ ...f, $order: i + 1 })
    )
    try {
      for (const orderedTwin of newOrderedTwinList) {
        const curTwin = featureQueryList.find((o) => o.$dtId === orderedTwin.$dtId)
        if (curTwin && orderedTwin.$order !== curTwin?.$order) {
          await createOrUpdateFeatureQuery(featureId, orderedTwin)
        }
      }
    } catch (err) {
      if (err instanceof Error) {
        throw new Error(err.message)
      }
    }
    return newOrderedTwinList
  }

  /** Delete featureQuery on the server */
  async function deleteFeatureQuery(featureId: string, featureQueryId: string) {
    await envApiService.deleteFeatureQuery(featureId, featureQueryId)
    delete featureQueries.value[featureId][featureQueryId]
  }

  /** Reset the current features */
  function resetCurrentFeature() {
    currentMainFeatureId.value = undefined
    currentDrawerFeatureId.value = undefined
  }

  /**
   * Force-fetch all feature queries for a feature, bypassing the bulk preload cache.
   * Use this for the explicit refresh path (toolbar refresh button) or admin pages
   * where the user expects up-to-date data. For all other read paths, prefer
   * {@link ensureFeatureQueriesLoaded} which awaits the bulk preload when available.
   */
  async function getFeatureQueries(featureId: string) {
    const _featureQueries = await envApiService.getFeatureQueries(featureId)
    const _featureQueriesObject: {
      [featureQueryId: string]: FeatureQueryTwin | DashboardTileTwin
    } = {}
    for (const featureQuery of _featureQueries) {
      _featureQueriesObject[featureQuery.$dtId] = featureQuery
    }
    featureQueries.value[featureId] = _featureQueriesObject
    return _featureQueries
  }

  /**
   * Bulk-preloads every feature's queries for the given environment in a single
   * round-trip. Concurrent invocations share the same in-flight promise so the
   * call is safe to fire repeatedly (e.g., from the router guard on every nav).
   * Populates {@link features} and {@link featureQueries} in one shot.
   */
  async function preloadAllFeatureQueries(envId?: string): Promise<void> {
    const _envId = envId || environmentId.value
    if (!_envId) return
    if (featureQueriesPreload.value) return featureQueriesPreload.value
    const generation = featureQueriesPreloadGeneration.value
    const promise = (async () => {
      const featuresWithQueries = await envApiService.getFeatures(_envId, { includeQueries: true })
      if (
        generation !== featureQueriesPreloadGeneration.value ||
        (environmentId.value !== undefined && environmentId.value !== _envId)
      )
        return
      const map: typeof featureQueries.value = {}
      for (const f of featuresWithQueries) {
        const queries = [...((f.$queries ?? []) as (FeatureQueryTwin | DashboardTileTwin)[])].sort(
          sortCallback
        )
        const byId: { [id: string]: FeatureQueryTwin | DashboardTileTwin } = {}
        for (const q of queries) byId[q.$dtId] = q
        map[f.$dtId] = byId
      }
      // Strip the embedded queries from the feature twins: they live in featureQueries
      // from here on, and keeping a second copy would double the persisted payload
      // (risking the localStorage quota on tile-heavy environments).
      const nextFeatures = featuresWithQueries.map((f) => {
        const feature = { ...f }
        delete feature.$queries
        return feature
      })
      // Assign only what actually changed. This runs as a background refresh on every
      // navigation, and the overwhelmingly common outcome is "identical to what we
      // already have" — reassigning then would invalidate every consumer, rewrite
      // localStorage and re-render dashboards for no visible difference.
      let changed = false
      if (!is.deepEqual(featureQueries.value, map)) {
        featureQueries.value = map
        changed = true
      }
      if (!is.deepEqual(features.value, nextFeatures)) {
        features.value = nextFeatures
        changed = true
      }
      if (changed) featureDataRevision.value++
      const { drawerConfigs: drawerConfigsList } = featureConfigs.value
      if (drawerConfigsList.length && drawerConfigsList[0].feature)
        currentDrawerFeatureId.value = drawerConfigsList[0].feature.$dtId
    })()
    featureQueriesPreload.value = promise
    try {
      await promise
    } finally {
      // Clear the in-flight marker so a future preload (after invalidation) can run.
      if (featureQueriesPreload.value === promise) featureQueriesPreload.value = undefined
    }
  }

  /**
   * Resolves once feature queries for {@link featureId} are present in the store.
   * Order of preference: cached → in-flight bulk preload → per-feature fallback.
   * Tile components should call this instead of {@link getFeatureQueries} so they
   * never trigger a duplicate API call after the preload is complete.
   */
  async function ensureFeatureQueriesLoaded(featureId: string): Promise<void> {
    if (featureQueries.value[featureId] && Object.keys(featureQueries.value[featureId]).length > 0)
      return
    if (featureQueriesPreload.value) {
      await featureQueriesPreload.value
      if (
        featureQueries.value[featureId] &&
        Object.keys(featureQueries.value[featureId]).length > 0
      )
        return
    }
    await getFeatureQueries(featureId)
  }

  /**
   * Clears every piece of state that's scoped to the current environment.
   * Called from the router guard on env switch — ensures stale tiles from
   * the previous env are not briefly visible while the new bulk preload runs.
   */
  function resetEnvironmentScopedState() {
    featureQueriesPreloadGeneration.value++
    features.value = []
    featureQueries.value = {}
    latestFeatureQueryMap.value = undefined
    featureQueriesPreload.value = undefined
  }

  function setLatestFeatureQuery(
    featureQueryMap: { featureId: string; featureQueryId: string } | undefined
  ) {
    latestFeatureQueryMap.value = featureQueryMap
  }

  function getFeatureQueryByCurrentMainFeature(featureQueryId: string) {
    if (!currentMainFeature.value) return undefined
    if (!featureQueries.value[currentMainFeature.value.$dtId]) return undefined
    return featureQueries.value[currentMainFeature.value.$dtId][featureQueryId]
  }

  function getFeatureQueryMainFeatureId(featureQueryId: string): string | undefined {
    const _featureMaps = Object.entries(featureQueries.value).map(([fId, fq]) => {
      return { featureId: fId, featureQueryIds: Object.keys(fq) }
    })
    for (const _featureMap of _featureMaps) {
      if (_featureMap.featureQueryIds.includes(featureQueryId)) return _featureMap.featureId
    }
  }

  // ==========================================================================
  // Actions - ImportMappings
  // ==========================================================================

  /** Get ImportMappings from server */
  async function getImportMappings() {
    importMappings.value = await envApiService.getImportMappings()
    return importMappings.value
  }

  /** Create or update a ImportMapping */
  async function createOrUpdateImportMapping(importMapping: ImportMapping) {
    await envApiService.createOrUpdateImportMapping(importMapping.$dtId, importMapping)
    const fIdx = importMappings.value.findIndex((f) => f.$dtId === importMapping.$dtId)
    if (fIdx > -1) importMappings.value[fIdx] = importMapping
    else importMappings.value.push(importMapping)
  }

  /** Delete ImportMapping on the server */
  async function deleteImportMapping(importMappingId: string) {
    await envApiService.deleteImportMapping(importMappingId)
    importMappings.value = importMappings.value.filter((w) => w.$dtId !== importMappingId)
  }

  /** Share ImportMapping with another environment */
  async function shareImportMapping(importMappingId: string, targetEnvironmentId: string) {
    await envApiService.shareImportMapping(importMappingId, targetEnvironmentId)
    const fIdx = importMappings.value.findIndex((f) => f.$dtId === importMappingId)
    if (fIdx > -1) importMappings.value[fIdx] = { ...importMappings.value[fIdx], isShared: true }
  }

  /** Get ImportMapping Twinmappings from server */
  async function getImportMappingTwinMappings(importMappingId: string) {
    twinMappings.value[importMappingId] =
      await envApiService.getImportMappingTwinMappings(importMappingId)
    return twinMappings.value[importMappingId]
  }

  /** Create or update an ImportMapping Twinmapping on the server */
  async function createOrUpdateImportMappingTwinMapping(
    importMappingId: string,
    twinMapping: TwinMapping
  ) {
    const newTwinMapping = await envApiService.createOrUpdateImportMappingTwinMapping(
      importMappingId,
      twinMapping.$dtId,
      twinMapping
    )
    if (twinMappings.value[importMappingId]) {
      const fIdx = twinMappings.value[importMappingId].findIndex(
        (f) => f.$dtId === newTwinMapping.$dtId
      )
      if (fIdx > -1) twinMappings.value[importMappingId][fIdx] = newTwinMapping
      else twinMappings.value[importMappingId].push(newTwinMapping)
    } else {
      twinMappings.value[importMappingId] = [newTwinMapping]
    }
    return newTwinMapping
  }

  /** Delete ImportMapping Twinmapping on the server */
  async function deleteImportMappingTwinMapping(importMappingId: string, twinMappingId: string) {
    await envApiService.deleteImportMappingTwinMapping(importMappingId, twinMappingId)
    twinMappings.value[importMappingId] = twinMappings.value[importMappingId].filter(
      (a) => a.$dtId !== twinMappingId
    )
  }

  async function processImportMappingDelayedOperations(
    importMappingId: string,
    twinMappingsList: TwinMapping[],
    deleteTwinMappingIds: string[]
  ) {
    await Promise.all(
      deleteTwinMappingIds.map((twinMappingId) =>
        deleteImportMappingTwinMapping(importMappingId, twinMappingId)
      )
    )
    await Promise.all(
      twinMappingsList.map((twinMapping) =>
        createOrUpdateImportMappingTwinMapping(importMappingId, twinMapping)
      )
    )
  }

  /** Create or update an importMapping with twinmappings on the server */
  async function createOrUpdateImportMappingWithTwinMappings(
    importMapping: ImportMapping,
    twinMappingsList: TwinMapping[],
    deleteTwinMappingIds: string[]
  ) {
    if (!importMapping) return
    await createOrUpdateImportMapping(importMapping)
    await new Promise<void>((resolve) => {
      setTimeout(async () => {
        await processImportMappingDelayedOperations(
          importMapping.$dtId,
          twinMappingsList,
          deleteTwinMappingIds
        )
        resolve()
      }, 5000)
    })
  }

  // ==========================================================================
  // Return Store
  // ==========================================================================

  return {
    // State
    environmentId,
    environments,
    features,
    featureQueries,
    featureDataRevision,
    importMappings,
    twinMappings,
    latestFeatureQueryMap,
    currentMainFeatureId,
    currentDrawerFeatureId,
    containerSas,
    roleDefinitions,

    // Computed - Environment & Roles
    environment,
    isSelfHosted,
    environmentRoles,
    environmentRoleNames,
    permissions,

    // Permission checks
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,

    // Permission-based computed
    editTwinAllowed,
    editEnvAllowed,

    // Features computed
    featureConfigs,
    featureTree,
    adminFeatureTree,
    currentMainFeature,
    currentMainFeatureConfig,
    currentDrawerFeature,
    currentDrawerFeatureConfig,
    latestFeatureQuery,

    // Actions - Environments
    loadRoleDefinitions,
    getEnvironment,
    getEnvironments,
    updateEnvironment,
    setFirstDrawerFeature,

    // Actions - Features
    getFeatures,
    createOrUpdateFeature,
    deleteFeature,
    createOrUpdateFeatureQuery,
    updateFeatureQuery,
    orderFeatureQuery,
    deleteFeatureQuery,
    resetCurrentFeature,
    getFeatureQueries,
    preloadAllFeatureQueries,
    ensureFeatureQueriesLoaded,
    resetEnvironmentScopedState,
    setLatestFeatureQuery,
    getFeatureQueryByCurrentMainFeature,
    getFeatureQueryMainFeatureId,

    // Actions - ImportMappings
    getImportMappings,
    createOrUpdateImportMapping,
    deleteImportMapping,
    shareImportMapping,
    getImportMappingTwinMappings,
    createOrUpdateImportMappingTwinMapping,
    deleteImportMappingTwinMapping,
    createOrUpdateImportMappingWithTwinMappings
  }
})

/* eslint-disable-next-line no-secrets/no-secrets -- false positive on the parameter name in the JSDoc below */
/**
 * Recursive function to get all children of a feature configuration
 * @param featureConfigs
 * @param level
 * @param parentId
 * @param hideInNavigation Hide features in navigation if hideInNavogation is true
 * @param includeOrphansAtRoot Show features whose parentFeatureId does not match
 * any known feature as root-level features (AB#4315). Without this, a feature
 * with a dangling parent (e.g. after an import that did not include the parent,
 * or a manually assigned non-existent parent) matches neither the root filter
 * nor any parent and silently disappears from AdminFeatures.
 */
const getFeatureChildrenRecursive = (
  featureConfigs: MainFeatureConfig[],
  level = 0,
  parentId?: string | undefined,
  hideInNavigation = false,
  includeOrphansAtRoot = false
): ExtendedMainFeatureConfig[] => {
  const knownIds =
    includeOrphansAtRoot && parentId === undefined
      ? new Set(
          featureConfigs.map((c) => c.feature?.$dtId).filter((id): id is string => id !== undefined)
        )
      : undefined
  return featureConfigs
    .filter(
      (featureConfig: MainFeatureConfig) =>
        (featureConfig.feature?.parentFeatureId === parentId ||
          (knownIds !== undefined &&
            featureConfig.feature?.parentFeatureId !== undefined &&
            !knownIds.has(featureConfig.feature.parentFeatureId))) &&
        (!hideInNavigation || featureConfig.feature?.hideInNavigation !== true)
    )
    .map(
      (featureConfig: MainFeatureConfig) =>
        ({
          ...featureConfig,
          ...(featureConfig.feature?.$dtId && {
            children: getFeatureChildrenRecursive(
              featureConfigs,
              level + 1,
              featureConfig.feature?.$dtId,
              hideInNavigation
            )
          }),
          level
        }) satisfies ExtendedMainFeatureConfig
    )
}
